<!--
  AudioWaveform.vue
  Draws a stretch of audio as bars mirrored around a center line — the clip in an AudioTrackPlayer lane and the
  lane of a LiveAudioTrack, so a recording looks the same while it's made and when it's played back.
  It draws seconds `start`..`end` of `peaks` (loudest sample per 1/peaksPerSecond s, 0..1) across its full width.
  Each bar covers a fixed slice of time counted from 0, so bars hold still while a scrolling window moves under
  them. Heights use a decibel scale so a quiet speaker still shows. Bars before `playedUntil` take the element's
  CSS `color` (the primary color unless a parent restyles it); the rest are a faded version of it.
-->
<template>
  <div class="audio-waveform" ref="wrap">
    <canvas ref="canvas" class="audio-waveform-canvas"></canvas>
  </div>
</template>

<script>
const BAR_WIDTH_PX = 2
const BAR_PITCH_PX = 3
// Silence still draws a hairline, so the clip's extent stays visible
const MIN_BAR_PX = 1
// Peaks this many dB below full scale draw as silence
const DB_RANGE = 48
const UNPLAYED_ALPHA = 0.35
// In peaks — far below one peak, far above float error
const SLICE_EPSILON = 1e-6

export default {
  name: 'AudioWaveform',
  props: {
    // Loudest sample per 1/peaksPerSecond s, 0..1. Float32Array or Array; may be partial while still decoding.
    peaks: { type: [Float32Array, Array], default: null },
    peaksPerSecond: { type: Number, required: true },
    // Seconds of `peaks` drawn across the full width
    start: { type: Number, default: 0 },
    end: { type: Number, required: true },
    // Seconds (same timeline as start/end) before which bars draw in the played color
    playedUntil: { type: Number, default: Infinity }
  },
  emits: ['resize'],
  created() {
    // Drawing state — not reactive
    this.cssWidth = 0
    this.cssHeight = 0
    this.bars = []
    this.playedBars = -1
    this.observer = null
  },
  computed: {
    // One watcher for everything that moves the bars, so a scrolling window lays out once per change
    layoutInputs() {
      return [this.peaks, this.peaksPerSecond, this.start, this.end]
    }
  },
  watch: {
    layoutInputs() {
      this.layout()
    },
    // Moving the playhead only recolors bars — redraw only when it crosses into another bar
    playedUntil() {
      if (this.countPlayed() !== this.playedBars) this.draw()
    }
  },
  mounted() {
    if (typeof ResizeObserver === 'function') {
      this.observer = new ResizeObserver(entries => {
        const rect = entries[0].contentRect
        this.resize(rect.width, rect.height)
      })
      this.observer.observe(this.$refs.wrap)
    }
    const rect = this.$refs.wrap.getBoundingClientRect()
    this.resize(rect.width, rect.height)
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    // A peak on the decibel scale, 0..1
    barLevel(peak) {
      if (!(peak > 0)) return 0
      return Math.min(1, Math.max(0, 1 + 20 * Math.log10(peak) / DB_RANGE))
    },
    resize(width, height) {
      if (width === this.cssWidth && height === this.cssHeight) return
      this.cssWidth = width
      this.cssHeight = height
      const canvas = this.$refs.canvas
      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      this.$emit('resize', { width, height })
      this.layout()
    },
    layout() {
      this.bars = this.computeBars()
      this.draw()
    },
    // The bars in the window: [{ x, time, level }], level 0..1, in time order. Nothing past the end of `peaks`
    // (still decoding) is drawn.
    computeBars() {
      const span = this.end - this.start
      if (!this.peaks || !(span > 0) || !(this.cssWidth > 0) || !(this.peaksPerSecond > 0)) return []
      const barSeconds = BAR_PITCH_PX * span / this.cssWidth
      const covered = this.peaks.length / this.peaksPerSecond
      const bars = []
      for (let index = Math.floor(this.start / barSeconds); index * barSeconds < this.end; index++) {
        const time = index * barSeconds
        if (time >= covered) break
        // The epsilon keeps float error (0.1 + 0.2) from pulling a neighbouring peak into the slice
        const from = Math.max(0, Math.floor(time * this.peaksPerSecond + SLICE_EPSILON))
        const to = Math.min(this.peaks.length,
          Math.max(from + 1, Math.ceil((time + barSeconds) * this.peaksPerSecond - SLICE_EPSILON)))
        let peak = 0
        for (let i = from; i < to; i++) {
          if (this.peaks[i] > peak) peak = this.peaks[i]
        }
        bars.push({ x: (time - this.start) / span * this.cssWidth, time, level: this.barLevel(peak) })
      }
      return bars
    },
    countPlayed() {
      let count = 0
      while (count < this.bars.length && this.bars[count].time < this.playedUntil) count++
      return count
    },
    draw() {
      this.playedBars = this.countPlayed()
      const canvas = this.$refs.canvas
      const ctx = canvas ? canvas.getContext('2d') : null
      if (!ctx) return
      const dpr = window.devicePixelRatio || 1
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, this.cssWidth, this.cssHeight)
      if (this.bars.length === 0) return

      const played = new Path2D()
      const unplayed = new Path2D()
      const middle = this.cssHeight / 2
      this.bars.forEach((bar, index) => {
        const height = Math.max(MIN_BAR_PX, bar.level * this.cssHeight)
        const path = index < this.playedBars ? played : unplayed
        path.rect(bar.x, middle - height / 2, BAR_WIDTH_PX, height)
      })
      ctx.fillStyle = getComputedStyle(canvas).color
      ctx.globalAlpha = 1
      ctx.fill(played)
      ctx.globalAlpha = UNPLAYED_ALPHA
      ctx.fill(unplayed)
      ctx.globalAlpha = 1
    }
  }
}
</script>

<style scoped>
.audio-waveform {
  position: relative;
  width: 100%;
  height: 100%;
  color: var(--color-primary);
}

.audio-waveform-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}
</style>
